import { Component, inject, DestroyRef, effect } from '@angular/core';

import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CapabilityConfigurationFormControl, CustomCapabilityConfigurationForm } from '../../../../../core/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeyValuePair } from '@vendasta/ai-assistants';

interface TransferCallConfig {
  // The phone number to transfer the call to
  number: string;
  // The criteria under which the call should be transferred to the number
  criteria: string;
}

@Component({
  selector: 'ai-transfer-call-capability-form',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    GalaxyFormFieldModule
],
  templateUrl: './transfer-call-capability-form.component.html',
  styleUrl: './transfer-call-capability-form.component.scss',
})
export class TransferCallCapabilityFormComponent extends CustomCapabilityConfigurationForm {
  protected readonly transferCallFormArray = new FormArray<
    FormGroup<{
      number: FormControl<string>;
      criteria: FormControl<string>;
    }>
  >([]);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    effect(() => {
      const configForm = this.configurationForm();
      this.initializeFormArray(configForm);
      this.setupValueChangesSubscription(configForm);
    });
  }

  private initializeFormArray(configForm: CapabilityConfigurationFormControl) {
    if (!configForm) {
      // Add default empty entry if no config form available
      this.addTransferCallEntry();
      return;
    }

    const rawConfig = configForm.value;
    if (!rawConfig || !Array.isArray(rawConfig) || rawConfig.length === 0) {
      // Add default empty entry if no configuration
      this.addTransferCallEntry();
      return;
    }

    const rawTransferCallConfigs = rawConfig.find((kv) => kv.key === 'transfer_call_configs');
    if (!rawTransferCallConfigs) {
      // Add default empty entry if no transfer call configs
      this.addTransferCallEntry();
      return;
    }

    try {
      const parsed = JSON.parse(rawTransferCallConfigs.value);

      if (!this.isTransferCallConfigArray(parsed)) {
        console.error('Invalid transfer call config format - data does not match expected structure');
        this.addTransferCallEntry();
        return;
      }

      if (parsed.length === 0) {
        this.addTransferCallEntry();
        return;
      }

      // Load existing configurations - TypeScript now knows 'parsed' is TransferCallConfig[]
      parsed.forEach((config) => {
        this.addTransferCallEntry(config.number, config.criteria);
      });
    } catch (error) {
      console.error('Failed to parse transfer call configs:', error);
      this.addTransferCallEntry();
    }
  }

  private setupValueChangesSubscription(configForm: CapabilityConfigurationFormControl) {
    if (configForm) {
      this.transferCallFormArray.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((formValues) => {
        if (formValues) {
          // Filter out entries where both number and criteria are empty
          // Also filter out entries with invalid phone numbers
          const filteredValues = formValues.filter((entry) => {
            const hasContent = (entry.number && entry.number.trim()) || (entry.criteria && entry.criteria.trim());
            const hasValidNumber = !entry.number || entry.number.trim() === '' || this.isValidPhoneNumber(entry.number);
            return hasContent && hasValidNumber;
          });

          if (filteredValues.length > 0) {
            configForm.setValue([
              new KeyValuePair({
                key: 'transfer_call_configs',
                value: JSON.stringify(filteredValues),
              }),
            ]);
            configForm.markAsDirty();
          } else {
            configForm.setValue(null);
          }
        }
      });
    }
  }

  protected addTransferCallEntry(number = '', criteria = '') {
    const entryForm = new FormGroup({
      number: new FormControl(number, {
        nonNullable: true,
        validators: [Validators.required, this.phoneNumberValidator()],
      }),
      criteria: new FormControl(criteria, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
    this.transferCallFormArray.push(entryForm);
  }

  protected removeTransferCallEntry(index: number) {
    if (this.transferCallFormArray.length > 1) {
      this.transferCallFormArray.removeAt(index);
    }
  }

  private phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Allow empty values (optional field)
      if (!value || value.trim() === '') {
        return null;
      }

      const trimmedValue = value.trim();

      // International format: + followed by 1-15 digits
      const internationalRegex = /^\+[1-9]\d{0,14}$/;

      // National format: 7-15 digits (allowing for various national formats)
      const nationalRegex = /^\d{7,15}$/;

      const isValid = internationalRegex.test(trimmedValue) || nationalRegex.test(trimmedValue);

      return isValid ? null : { phoneNumber: { value: value } };
    };
  }

  private isValidPhoneNumber(phoneNumber: string): boolean {
    if (!phoneNumber) {
      return false;
    }

    const trimmedValue = phoneNumber.trim();

    // International format: + followed by 1-15 digits
    const internationalRegex = /^\+[1-9]\d{0,14}$/;

    // National format: 7-15 digits
    const nationalRegex = /^\d{7,15}$/;

    return internationalRegex.test(trimmedValue) || nationalRegex.test(trimmedValue);
  }

  private isTransferCallConfig(obj: unknown): obj is TransferCallConfig {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'number' in obj &&
      'criteria' in obj &&
      typeof (obj as any).number === 'string' &&
      typeof (obj as any).criteria === 'string'
    );
  }

  private isTransferCallConfigArray(obj: unknown): obj is TransferCallConfig[] {
    return Array.isArray(obj) && obj.every((item) => this.isTransferCallConfig(item));
  }

  protected getFieldErrorKey(control: AbstractControl, fieldType: 'number' | 'criteria'): string | null {
    if (!control.errors || !control.touched) {
      return null;
    }

    if (control.hasError('required')) {
      return fieldType === 'number'
        ? 'AI_ASSISTANT.SETTINGS.TRANSFER_CALL.TRANSFER_ERRORS.PHONE_NUMBER_REQUIRED'
        : 'AI_ASSISTANT.SETTINGS.TRANSFER_CALL.TRANSFER_ERRORS.CRITERIA_REQUIRED';
    }

    if (control.hasError('phoneNumber')) {
      return 'AI_ASSISTANT.SETTINGS.TRANSFER_CALL.TRANSFER_ERRORS.PHONE_NUMBER_INVALID';
    }

    return null;
  }
}
